import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import { POrderLineDto, POrderDetailsDto, InboundShipmentSummaryDto } from '../types/porders';
import { POrderLineWithHeaderDto } from '../types/purchaseOrderLines';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class POrderLineService {

  /**
   * Søk på tvers av alle åpne PO-linjer etter varenummer eller strekkode.
   * Brukes i Modus 2 (Åpent mottak) hvor operatør skanner varer uten å velge ordre først.
   * Returnerer linjer hvor RemainingQuantity > 0 og OrderStatus < 50.
   */
  async searchOpenPOLinesByItem(searchTerm: string): Promise<POrderLineWithHeaderDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/lines/search-open?searchTerm=${encodeURIComponent(searchTerm)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search open PO lines for "${searchTerm}": ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter alle linjer for en spesifikk ordre.
   * Kansellerte linjer (LineStatus 80) skjules som standard (mottaksbildene);
   * adminbilder sender includeCancelled=true for å vise dem med merking.
   */
  async getOrderLines(orderNo: number, includeCancelled: boolean = false): Promise<POrderLineDto[]> {
    const queryParams = includeCancelled ? '?includeCancelled=true' : '';
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/${orderNo}/lines${queryParams}`);

    if (!response.ok) {
      throw new Error(`Failed to get order lines for order ${orderNo}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Kanseller en ordrelinje (LineStatus → 80). Kun linjer uten mottak.
   */
  async cancelOrderLine(orderNo: number, lineNo: number): Promise<void> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/porders/${orderNo}/lines/${lineNo}/cancel`, {}
    );

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(extractApiError(body, response, `Failed to cancel order line ${lineNo} for order ${orderNo}`));
    }
  }

  /**
   * Gjenåpne en kansellert ordrelinje (LineStatus 80 → 0).
   */
  async reopenOrderLine(orderNo: number, lineNo: number): Promise<void> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/porders/${orderNo}/lines/${lineNo}/reopen`, {}
    );

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(extractApiError(body, response, `Failed to reopen order line ${lineNo} for order ${orderNo}`));
    }
  }

  /**
   * Henter ordre med alle linjer
   */
  async getOrderDetails(orderNo: number): Promise<POrderDetailsDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/${orderNo}/details`);
    
    if (!response.ok) {
      throw new Error(`Failed to get order details for order ${orderNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Oppdaterer en ordrelinje
   */
  async updateOrderLine(orderNo: number, lineNo: number, line: Partial<POrderLineDto>): Promise<POrderLineDto> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/porders/${orderNo}/lines/${lineNo}`,
      line
    );

    if (!response.ok) {
      throw new Error(`Failed to update order line ${lineNo} for order ${orderNo}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  // ========== Inbound Shipment Methods (Modus 3) ==========

  /**
   * Henter alle aktive inbound shipments med aggregert informasjon.
   * Brukes i Modus 3 (Inbound Shipment) for å vise liste over forsendelser.
   * Returnerer forsendelser hvor minst én linje har gjenværende kvantum > 0.
   */
  async getActiveShipments(warehouse?: string): Promise<InboundShipmentSummaryDto[]> {
    // TW 36430604: følger valgt lager; backend overstyrer for låste profiler
    const query = warehouse ? `?warehouse=${encodeURIComponent(warehouse)}` : '';
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/shipments${query}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get active shipments: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Henter alle PO-linjer som tilhører en spesifikk inbound shipment.
   * Brukes i Modus 3 (Inbound Shipment) og Modus 4 (Bilmottak) for å vise detaljer.
   * @param includeCompleted Hvis true, inkluderes ferdigmottatte linjer (brukes av bilmottak)
   */
  async getShipmentLines(shipmentNo: string, includeCompleted: boolean = false): Promise<POrderLineWithHeaderDto[]> {
    const queryParams = includeCompleted ? '?includeCompleted=true' : '';
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/shipments/${encodeURIComponent(shipmentNo)}/lines${queryParams}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get shipment lines for "${shipmentNo}": ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Søk etter åpne PO-linjer etter varenummer innenfor en spesifikk inbound shipment.
   * Brukes i Modus 3 (Inbound Shipment) når operatør skanner varer.
   */
  async searchShipmentLinesByItem(shipmentNo: string, searchTerm: string): Promise<POrderLineWithHeaderDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/shipments/${encodeURIComponent(shipmentNo)}/lines/search?searchTerm=${encodeURIComponent(searchTerm)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search shipment "${shipmentNo}" for "${searchTerm}": ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}

export const pOrderLineService = new POrderLineService();
